import React from 'react';
import PropTypes from 'prop-types';
import {Utils, ViewHelpers} from 'poly-js-utils';
import classNames from 'classnames';
import {connectToJSContext, RA_73_STERILIZE_HTML_STRING } from '../utils.jsx';
import GoogleMap, {loadMap} from './google_map.jsx';
import { LOCATIONS } from '../constants';
import { phoneFormat } from '../../common/utils';
import LazyImg from '../lazy_img/index.jsx';

const {formatHref, printAddress} = ViewHelpers;

const appendToClass = (className, key) => {
    return className + key;
};

class Location extends React.Component {
    static defaultProps = {
        suboffices: LOCATIONS.DEFAULT_PROPS.SUBOFFICES
    };

    state = {
        offices: [this.props.mainOffice, ...this.props.suboffices],
        activeOffice: 0
    };

    setActiveTab = (idx) => {
        this.setState(() => ({
            activeOffice: idx
        }));
    };

    render() {
        const pref = this.props.formatPreference;
        const heading = this.props.suboffices.length > 0 ? this.props.locationSubMult : this.props.locationSubSingle;

        const tabList = this.state.offices.map((office, idx) => (
            <Tab
                officeTitle={office.officeTitle}
                officeId={appendToClass("office-", idx)}
                officeMapId={appendToClass("office-map-", idx)}
                address={printAddress(office, LOCATIONS.OFFICE_ADDRESS_FIELDS)}
                latlng={office.latlng}
                street={office.street}
                aria-controls={`office-${idx}`}
                hrefId={appendToClass("#office-", idx)}
                activeClass={this.state.activeOffice === idx ? "office-link active" : "office-link"}
                key={idx}
                setActiveTab={() => this.setActiveTab(idx)}
            />
        ));

        const tabListContent = this.state.offices.map((office, idx) => (
            <TabPanel
                office={office}
                key={idx}
                officeId={appendToClass("office-", idx)}
                officeMapId={appendToClass("office-map-", idx)}
                activeClass={this.state.activeOffice === idx ? "tab-pane fade in active" : "tab-pane fade"}
                locationPhone={this.props.locationPhone}
                locationFax={this.props.locationFax}
                locationTollFree={this.props.locationTollFree}
                locationCell={this.props.locationCell}
                locationEmail={this.props.locationEmail}
                locationOfficeHours={this.props.locationOfficeHours}
                locationAddress={this.props.locationAddress}
                officePhoneHref={formatHref("tel", phoneFormat(office.phone, pref).link)}
                officePhone={phoneFormat(office.phone, pref).readable}
                officeFaxHref={formatHref("tel", phoneFormat(office.fax, pref).link)}
                officeFax={phoneFormat(office.fax, pref).readable}
                officeTollFreeHref={formatHref("tel", phoneFormat(office.tollFree, pref).link)}
                officeTollFree={phoneFormat(office.tollFree, pref).readable}
                officeCellHref={formatHref("tel", phoneFormat(office.cell, pref).link)}
                officeCell={phoneFormat(office.cell, pref).readable}
                officeEmailHref={formatHref("mailto", office.email)}
                officeEmail={office.email}
                address={printAddress(office, LOCATIONS.OFFICE_ADDRESS_FIELDS)}
                externalWindowLinkAriaText={this.props.externalWindowLinkAriaText}
            />
        ));

        return (
            <section className="hs-locations hs-section" id="location">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-header">
                                <h2 className="section-headline office-heading">
                                    {heading}
                                </h2>
                                <ul className="nav nav-tabs" role="tablist">
                                    {tabList}
                                </ul>
                            </div>
                            <div className="tab-content">
                                {tabListContent}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}
class Tab extends React.Component {

    clickTab = (e) => {
        e.preventDefault();
        this.props.setActiveTab();
        loadMap(
            this.props.officeId,
            this.props.officeMapId,
            this.props.latlng,
            this.props.address
        );
    };

    render() {
        return (
            <li role="presentation" className={this.props.activeClass}>
                <a
                    className="color-accent office-location-tab"
                    href={this.props.hrefId}
                    role="tab"
                    data-toggle="tab"
                    onClick={this.clickTab}
                >
                    <span
                        className="office-location-tab__office-name"
                    >
                        {this.props.officeTitle || this.props.street}
                    </span>
                </a>
            </li>
        );
    }
}

const TabPanel = (props) => {
    const isBottomOfficeImgOrientation = props.office.officeImageOrientation === 'bottom';
    const headlineContainerClass = classNames({
        'col-sm-6': !isBottomOfficeImgOrientation,
        'col-sm-3': isBottomOfficeImgOrientation
    });
    return (
        <div role="tabpanel" className={props.activeClass} id={props.officeId} aria-label="Google Maps" tabIndex="0">
            <GoogleMap
                latlng={props.office.latlng}
                image={!isBottomOfficeImgOrientation ? props.office.officeImage : null}
                street={props.office.street}
                officeId={props.officeId}
                officeMapId={props.officeMapId}
                address={props.address}
            />
            <div itemScope itemType="http://schema.org/LocalBusiness" className="location__info">
                <div className="row">
                    {isBottomOfficeImgOrientation && props.office.officeImage &&
                        <div className="col-sm-3">
                            <LazyImg
                                className="office-location__office-image office-location__bottom-img"
                                src={props.office.officeImage}
                                alt={props.office.street}
                            />
                        </div>
                    }
                    <div className={headlineContainerClass}>
                        <h3 className="module-headline office-name">
                            {props.office.officeTitle || "Contact"}
                        </h3>
                        <InfoList
                            locationPhone={props.locationPhone}
                            locationFax={props.locationFax}
                            locationTollFree={props.locationTollFree}
                            locationCell={props.locationCell}
                            locationEmail={props.locationEmail}
                            officePhoneHref={props.officePhoneHref}
                            officePhone={props.officePhone}
                            officeFaxHref={props.officeFaxHref}
                            officeFax={props.officeFax}
                            officeTollFreeHref={props.officeTollFreeHref}
                            officeTollFree={props.officeTollFree}
                            officeCellHref={props.officeCellHref}
                            officeCell={props.officeCell}
                            officeEmailHref={props.officeEmailHref}
                            officeEmail={props.officeEmail}
                        />
                    </div>
                    {props.office.officeHours &&
                        <div className="col-sm-3">
                            <h3
                                className="module-headline office-location__office-hours-label"
                            >
                                {props.locationOfficeHours}
                            </h3>
                            <div
                                className="office-location__office-hours"
                                dangerouslySetInnerHTML={{
                                    __html: RA_73_STERILIZE_HTML_STRING(props.office.officeHours)
                                }}
                            />
                        </div>
                    }
                    <div itemProp="address" itemScope itemType="http://schema.org/PostalAddress" className="col-sm-3">
                        <h3 className="module-headline office-location__address-label">{props.locationAddress}</h3>
                        <a
                            href={ViewHelpers.googleMapsDirections(props.office)}
                            target="_blank"
                            rel="noopener"
                            data-click-type="driving-directions"
                            data-office-name={props.office.officeTitle || props.officeId}
                        >
                            <p className="color-accent address office-location__address">
                                <span
                                    itemProp="streetAddress office-location__street-address"
                                >
                                    {props.office.street}
                                </span>
                                <br />
                                {props.office.suite &&
                                    <span className="office-location__suite">
                                        {props.office.suite}<br />
                                    </span>
                                }
                                <span itemProp="addressLocality office-location__city">{props.office.city}</span>,
                                <span itemProp="addressRegion office-location__state"> {props.office.state}</span>
                                <span itemProp="postalCode office-location__zip-code"> {props.office.zipCode}</span>
                                {props.office.country &&
                                    <span>
                                        <br />
                                        <span itemProp="addressCountry office-location__country">
                                            {props.office.country}
                                        </span>
                                    </span>
                                }
                                <span className="sr-only">({props.externalWindowLinkAriaText})</span>
                            </p>
                        </a>
                        {/* This is invisible and formatted for geocoding */}
                        <span className="address-text" data-latlng={props.office.latlng}>
                            {printAddress(props.office, LOCATIONS.OFFICE_ADDRESS_FIELDS)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const InfoList = (props) => (
    <dl className="info-list">
        {props.officePhone &&
            [
                <dt key={props.locationPhone} className="light info-list__phone-label">{props.locationPhone}:</dt>,
                <dd key={props.officePhone}>
                    <a
                        itemType="telephone"
                        className="contact-link color-accent info-list__phone-number"
                        href={props.officePhoneHref}
                        aria-label={props.officePhone}
                        data-click-type="contact"
                        data-contact-method="phone"
                        data-contact-value={props.officePhone}
                    >
                        {props.officePhone}
                    </a>
                </dd>
            ]
        }
        {props.officeFax &&
            [
                <dt key={props.locationFax} className="light info-list__fax-label">{props.locationFax}:</dt>,
                <dd key={props.officeFax} className="info-list__fax-number">{props.officeFax}</dd>
            ]
        }
        {props.officeTollFree &&
            [
                <dt
                    key={props.locationTollFree}
                    className="light info-list__toll-free-label"
                >{props.locationTollFree}:</dt>,
                <dd key={props.officeTollFree}>
                    <a
                        className="contact-link color-accent info-list__toll-free-number"
                        href={props.officeTollFreeHref}
                        aria-label={props.officeTollFree}
                        data-click-type="contact"
                        data-contact-method="phone"
                        data-contact-value={props.officeTollFree}
                    >
                        {props.officeTollFree}
                    </a>
                </dd>
            ]
        }
        {props.officeCell &&
            [
                <dt key={props.locationCell} className="light info-list__cell-label">{props.locationCell}:</dt>,
                <dd key={props.officeCell}>
                    <a
                        className="contact-link color-accent info-list__cell-number"
                        href={props.officeCellHref}
                        aria-label={props.officeCell}
                        data-click-type="contact"
                        data-contact-method="phone"
                        data-contact-value={props.officeCell}
                    >
                        {props.officeCell}
                    </a>
                </dd>
            ]
        }
        {props.officeEmail &&
            [
                <dt key={props.locationEmail} className="light info-list__email-label">{props.locationEmail}:</dt>,
                <dd key={props.officeEmail}>
                    <a
                        className="contact-link color-accent info-list__email"
                        href={props.officeEmailHref}
                        aria-label={props.officeEmail}
                        data-click-type="contact"
                        data-contact-method="email"
                        data-contact-value={props.officeEmail}
                    >
                        {props.officeEmail}
                    </a>
                </dd>
            ]
        }
    </dl>
);

Location.propTypes = {
    mainOffice: PropTypes.shape({
        cell: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
        fax: PropTypes.string,
        latlng: PropTypes.array,
        email: PropTypes.string,
        officeHours: PropTypes.string,
        officeImage: PropTypes.string,
        officeTitle: PropTypes.string,
        phone: PropTypes.string,
        state: PropTypes.string,
        street: PropTypes.string,
        suite: PropTypes.string,
        tollFree: PropTypes.string,
        zipCode: PropTypes.string
    }),
    suboffices: PropTypes.arrayOf(PropTypes.shape({
        cell: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
        fax: PropTypes.string,
        latlng: PropTypes.array,
        email: PropTypes.string,
        officeHours: PropTypes.string,
        officeImage: PropTypes.string,
        officeTitle: PropTypes.string,
        phone: PropTypes.string,
        state: PropTypes.string,
        street: PropTypes.string,
        suite: PropTypes.string,
        tollFree: PropTypes.string,
        zipCode: PropTypes.string
    })),
    locationPhone: PropTypes.string,
    locationFax: PropTypes.string,
    locationTollFree: PropTypes.string,
    locationCell: PropTypes.string,
    locationEmail: PropTypes.string,
    locationOfficeHours: PropTypes.string,
    locationAddress: PropTypes.string,
    locationSubMult: PropTypes.string,
    locationSubSingle: PropTypes.string,
    externalWindowLinkAriaText: PropTypes.string
};

export {Location};

const mapStateToProps = (state) => {
    const labels = state.theme_data.labels || {};
    const formatPhone = state.theme_data.format_phone_number;

    return {
        mainOffice: {
            cell: state.profile.phones.cell,
            city: state.profile.address.city,
            country: state.profile.address.country,
            fax: state.profile.phones.fax,
            latlng: state.profile.latlng,
            email: state.profile.email,
            officeHours: state.profile.office_hours,
            officeImage: state.profile.office_image,
            officeImageOrientation: state.theme_data.force_office_img_orientation
                ? state.theme_data.force_office_img_orientation[0]
                : (state.profile.office_img_orientation && state.profile.office_img_orientation[0]) || "top",
            officeTitle: state.profile.office_title,
            phone: state.profile.phones.phone,
            state: state.profile.address.state,
            street: state.profile.address.street,
            suite: state.profile.address.suite,
            tollFree: state.profile.phones.toll_free,
            zipCode: state.profile.address.zip_code
        },
        suboffices: state.profile.suboffices && state.profile.suboffices.map(office => {
            const officeImageOrientation = state.theme_data.force_office_img_orientation
                ? state.theme_data.force_office_img_orientation[0]
                : (office.office_img_orientation && office.office_img_orientation[0]) || "top";

            return {
                cell: office.cell,
                city: office.city,
                country: office.country,
                fax: office.fax,
                email: office.office_email,
                officeHours: office.office_hours,
                officeImage: office.office_image,
                officeImageOrientation: officeImageOrientation,
                officeTitle: office.office_title,
                phone: office.phone,
                state: office.state,
                street: office.street,
                suite: office.suite,
                tollFree: office.toll_free,
                zipCode: office.zip_code
            };
        }),
        formatPreference: !formatPhone ? true : Utils.selectBoxIsTrue(formatPhone),
        locationPhone: labels.location_phone || LOCATIONS.LABELS.PHONE,
        locationFax: labels.location_fax || LOCATIONS.LABELS.FAX,
        locationTollFree: labels.location_toll_free || LOCATIONS.LABELS.TOLL_FREE,
        locationCell: labels.location_cell || LOCATIONS.LABELS.CELL,
        locationEmail: labels.location_email || LOCATIONS.LABELS.EMAIL,
        locationOfficeHours: labels.location_office_hours || LOCATIONS.LABELS.OFFICE_HOURS,
        locationAddress: labels.location_address || LOCATIONS.LABELS.ADDRESS,
        locationSubMult: labels.locations_suboffices_header_multiple || LOCATIONS.LABELS.HEADER_MULTIPLE_SUBOFFICES,
        locationSubSingle: labels.locations_suboffices_header_single || LOCATIONS.LABELS.HEADER_SINGLE_SUBOFFICE,
        externalWindowLinkAriaText: labels.externalWindowLinkAriaText
    };
};

export {mapStateToProps};
export default connectToJSContext(mapStateToProps)(Location);



// WEBPACK FOOTER //
// ./components/locations/index.jsx